import { FC, useState } from "react";
import { Popover } from "antd";
import { useLocation } from "react-router-dom";

import { ReactComponent as LogoDM } from "assets/images/auth/logoDM.svg";
import { ReactComponent as LogoPP } from "assets/images/auth/logoPP.svg";
import { ReactComponent as LogoHC } from "assets/images/auth/logoHC.svg";
import { ReactComponent as LogoGPS } from "assets/images/auth/logoGPS.svg";
import { ReactComponent as LogoAPM } from "assets/images/auth/logoAPM.svg";
import { ReactComponent as LogoutIcon } from "assets/images/app-switcher/logout.svg";
import { ReactComponent as PersonIcon } from "assets/images/app-switcher/person.svg";
import { ReactComponent as ShifterIcon } from "assets/images/app-switcher/shifter.svg";
import { ReactComponent as ArrowDownIcon } from "assets/images/app-switcher/arrow-down.svg";

import defaultPictureSrc from "assets/images/profile/avatar.svg";
import arootahSrc from "assets/images/app-switcher/arootah.png";

import {
  Box,
  Wrapper,
  AppItem,
  AppLink,
  AppItemTitle,
  Toggle,
  ProfileImg,
  ProfileUser,
  ProfileSection,
  FooterSection,
  LogoutButton,

  SwitcherWrap,
  ShifterWrap,
  ShifterText,

  ActiveBadge
} from './styled';

// @ts-ignore
import customStyles from './global.module.scss';

import {
  APM_PATH,
  JOURNEY_PATH,
  PROFILE_PATH,
} from "utils/routes";

import {
  USER_SERVICE_APP_URL,
  HAT_APP_URL,
  PP_APP_URL
} from "config";

interface IProps {
  photo: string;
  name?: string;
  onLogout: () => void;
}

const AppSwitcher: FC<IProps> = ({ photo, name, onLogout }) => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const isAPM = pathname.includes(APM_PATH);

  // Extract domain from full URLs for use in href attributes
  const authURL = USER_SERVICE_APP_URL.replace('https://', '');
  const hcURL = HAT_APP_URL.replace('https://', '');
  const ppURL = PP_APP_URL.replace('https://', '');

  const hide = () => {
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  return (
    <Popover
      overlayClassName={customStyles.popover}
      placement="bottomLeft"
      content={
        <Box>
          <Wrapper>
            <ProfileSection>
              <ProfileImg src={photo || defaultPictureSrc} />
              <ProfileUser>{name || "Guest"}</ProfileUser>
            </ProfileSection>

            <AppItem to={`${isAPM ? APM_PATH : JOURNEY_PATH}${PROFILE_PATH}`} onClick={hide}>
              <PersonIcon />
              <AppItemTitle>My profile</AppItemTitle>
            </AppItem>

            <SwitcherWrap>
              <ShifterWrap>
                <ShifterIcon />
                <ShifterText>Switch Apps</ShifterText>
              </ShifterWrap>

              <AppLink href={`https://${authURL}`} onClick={hide}>
                <img src={arootahSrc} alt="" />
                <AppItemTitle $bold>Arootah Home</AppItemTitle>
              </AppLink>
            </SwitcherWrap>

            <AppItem to={'/'} onClick={hide} $active={!isAPM}>
              <LogoGPS />
              <AppItemTitle>Goal Manager</AppItemTitle>
              {isAPM ? null : <ActiveBadge>Active</ActiveBadge>}
            </AppItem>

            <AppItem to={APM_PATH} onClick={hide} $active={isAPM}>
              <LogoAPM />
              <AppItemTitle>Time Manager</AppItemTitle>
              {isAPM ? <ActiveBadge>Active</ActiveBadge> : null}
            </AppItem>

            <AppLink href={`https://${hcURL}`}>
              <LogoHC />
              <AppItemTitle>Habit Manager</AppItemTitle>
            </AppLink>

            <AppLink href={`https://${ppURL}/decision-manager`}>
              <LogoDM />
              <AppItemTitle>Decision Manager</AppItemTitle>
            </AppLink>

            <AppLink href={`https://${ppURL}/project-planner`}>
              <LogoPP />
              <AppItemTitle>Project Manager</AppItemTitle>
            </AppLink>

            <FooterSection>
              <LogoutButton onClick={onLogout}>
                <LogoutIcon />
                Log Out
              </LogoutButton>
            </FooterSection>
          </Wrapper>
        </Box>
      }
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
    >
      <Toggle>
        <ProfileImg src={photo || defaultPictureSrc} $shadow />
        <ArrowDownIcon />
      </Toggle>
    </Popover>
  );
}

export default AppSwitcher;
